import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../services/auth.service';
import { AgentCommissionService } from '../../services/agentCommission.service';
import { CommercialPartyService } from '../../services/commercialParty.service';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { money, triggerDownload } from '../statutoryRegisters/shared';
import { parseContentDispositionFilename } from '../../utils/contentDisposition';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { ICommercialParty } from '../../interfaces/commercialParty.interface';
import {
    ICommissionAgentBlock, ICommissionRegister, ICommissionSettlement,
} from '../../interfaces/agentCommission.interface';

const DIRECTION_TEXT = {
    PAYABLE: { settled: 'Paid', outstanding: 'Still to pay', caption: 'Commission you owe your buying agents.' },
    RECEIVABLE: { settled: 'Received', outstanding: 'Still to receive', caption: 'Commission you earn as a buying house.' },
};

interface SettleTarget { agent: ICommissionAgentBlock; orderId?: string; orderNumber?: string; currency?: string }

export default function AgentCommissionPage() {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.AGENT_COMMISSION_MANAGE);

    const [register, setRegister] = useState<ICommissionRegister | null>(null);
    const [settlements, setSettlements] = useState<ICommissionSettlement[]>([]);
    const [agents, setAgents] = useState<ICommercialParty[]>([]);
    const [agentId, setAgentId] = useState('');
    const [showVoided, setShowVoided] = useState(false);
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState<SettleTarget | null>(null);
    const [voiding, setVoiding] = useState<ICommissionSettlement | null>(null);

    const load = useCallback(async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setLoading(true);
        try {
            const [r, s] = await Promise.all([
                AgentCommissionService.register(inst, agentId || undefined),
                AgentCommissionService.settlements(inst, agentId || undefined, undefined, showVoided),
            ]);
            setRegister(r);
            setSettlements(s);
        } catch (e) {
            toast.showApiError(e, 'Failed to load the commission register');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId, showVoided]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        CommercialPartyService.list(inst, { type: 'BUYING_AGENT', size: 200, activeOnly: true })
            .then(p => setAgents(p.content)).catch(() => setAgents([]));
    }, []);

    const text = DIRECTION_TEXT[register?.direction ?? 'PAYABLE'];

    const download = async (a: ICommissionAgentBlock) => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        try {
            const res = await AgentCommissionService.downloadStatement(inst, a.agentPartyId);
            triggerDownload(res.data, parseContentDispositionFilename(
                res.headers?.['content-disposition'], 'commission-statement.xlsx'));
        } catch (e) {
            toast.showApiError(e, 'Failed to download the statement');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.agentCommission.page">
            <Typography variant="h5" sx={{ mb: 1 }}>Agent Commission</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{text.caption}</Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="commercial.agentCommission.filter">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 380 }}>
                        <SearchableSelect<ICommercialParty> label="Agent" size="small" options={agents} value={agentId}
                            getOptionLabel={a => a.name} onChange={id => setAgentId(id)} />
                    </Box>
                    <Button size="small" onClick={() => setShowVoided(v => !v)} data-help="commercial.agentCommission.showVoided">
                        {showVoided ? 'Hide voided settlements' : 'Show voided settlements'}
                    </Button>
                </Box>
            </Paper>

            {loading ? <CircularProgress /> : (
                <>
                    {register && register.agents.length === 0 && (
                        <Alert severity="info">No orders with an agent commission yet. Set the agent and the commission percent on the buyer order.</Alert>
                    )}
                    {register?.agents.map(a => (
                        <Paper key={a.agentPartyId} variant="outlined" sx={{ p: 2, mb: 3 }} data-testid={`agent-${a.agentPartyId}`}
                            data-help-section="commercial.agentCommission.agent">
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
                                <Typography variant="h6">{a.agentName}</Typography>
                                <Stack direction="row" gap={1}>
                                    {canManage && (
                                        <Button size="small" startIcon={<AddIcon />} data-help="commercial.agentCommission.settleOnAccount"
                                            onClick={() => setTarget({ agent: a })}>Settle on account</Button>
                                    )}
                                    <Button size="small" startIcon={<FileDownloadIcon />} data-help="commercial.agentCommission.statement"
                                        onClick={() => download(a)}>Statement</Button>
                                </Stack>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Currency</TableCell>
                                            <TableCell align="right" data-help="commercial.agentCommission.col.expected">Expected</TableCell>
                                            <TableCell align="right" data-help="commercial.agentCommission.col.accrued">Earned on invoices</TableCell>
                                            <TableCell align="right">{text.settled}</TableCell>
                                            <TableCell align="right">{text.outstanding}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {a.totals.map(t => (
                                            <TableRow key={t.currencyCode} sx={{ '& td': { fontWeight: 'bold' } }}>
                                                <TableCell>{t.currencyCode}</TableCell>
                                                <TableCell align="right">{money(t.expected)}</TableCell>
                                                <TableCell align="right">{money(t.accrued)}</TableCell>
                                                <TableCell align="right">{money(t.settled)}</TableCell>
                                                <TableCell align="right">{money(t.outstanding)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Buyer</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Cur.</TableCell>
                                            <TableCell align="right">Order value</TableCell>
                                            <TableCell align="right">%</TableCell>
                                            <TableCell align="right">Expected</TableCell>
                                            <TableCell align="right">Earned</TableCell>
                                            <TableCell align="right">{text.settled}</TableCell>
                                            <TableCell align="right">{text.outstanding}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {a.orders.map(o => (
                                            <TableRow key={o.orderId} hover>
                                                <TableCell>{o.orderNumber}</TableCell>
                                                <TableCell>{o.buyerName}</TableCell>
                                                <TableCell>{o.status}</TableCell>
                                                <TableCell>{o.currencyCode}</TableCell>
                                                <TableCell align="right">{money(o.orderValue)}</TableCell>
                                                <TableCell align="right">{o.commissionPercent}</TableCell>
                                                <TableCell align="right">{money(o.expected)}</TableCell>
                                                <TableCell align="right">
                                                    {money(o.accrued)}
                                                    {o.foreignCurrencyInvoices > 0 && (
                                                        <Chip size="small" color="warning" sx={{ ml: 0.5 }} data-help="commercial.agentCommission.foreignInvoices"
                                                            label={`${o.foreignCurrencyInvoices} invoice(s) in another currency not counted`} />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{money(o.settled)}</TableCell>
                                                <TableCell align="right">{money(o.outstanding)}</TableCell>
                                                <TableCell align="right">
                                                    {canManage && (
                                                        <Button size="small" data-help="commercial.agentCommission.settle"
                                                            onClick={() => setTarget({ agent: a, orderId: o.orderId, orderNumber: o.orderNumber, currency: o.currencyCode })}>
                                                            Settle
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ))}

                    <Paper variant="outlined" sx={{ p: 2 }} data-help-section="commercial.agentCommission.settlements">
                        <Typography variant="h6" sx={{ mb: 1 }}>Settlements</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Agent</TableCell>
                                        <TableCell>Order</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Cur.</TableCell>
                                        <TableCell>Reference</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {settlements.length === 0 && (
                                        <TableRow><TableCell colSpan={7} align="center">No settlements recorded.</TableCell></TableRow>
                                    )}
                                    {settlements.map(s => (
                                        <TableRow key={s.id} sx={s.voided ? { opacity: 0.55, textDecoration: 'line-through' } : undefined}>
                                            <TableCell>{formatDate(s.settledDate)}</TableCell>
                                            <TableCell>{s.agentName}</TableCell>
                                            <TableCell>{s.orderNumber ?? 'On account'}</TableCell>
                                            <TableCell align="right">{money(s.amount)}</TableCell>
                                            <TableCell>{s.currencyCode}</TableCell>
                                            <TableCell>{s.reference ?? ''}</TableCell>
                                            <TableCell align="right" sx={{ textDecoration: 'none' }}>
                                                {s.voided ? <Chip size="small" label={`Voided: ${s.voidReason ?? ''}`} />
                                                    : canManage && (
                                                        <Button size="small" color="warning" data-help="commercial.agentCommission.void"
                                                            onClick={() => setVoiding(s)}>Void</Button>
                                                    )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            <SettleDialog target={target} direction={register?.direction ?? 'PAYABLE'}
                onClose={() => setTarget(null)} onSaved={load} />
            <VoidDialog settlement={voiding} onClose={() => setVoiding(null)} onSaved={load} />
        </Box>
    );
}

function SettleDialog({ target, direction, onClose, onSaved }: {
    target: SettleTarget | null; direction: 'PAYABLE' | 'RECEIVABLE'; onClose: () => void; onSaved: () => void;
}) {
    const toast = useToast();
    const [date, setDate] = useState(toLocalIsoDate(new Date()));
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [reference, setReference] = useState('');
    const [remarks, setRemarks] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!target) return;
        setDate(toLocalIsoDate(new Date())); setAmount(''); setReference(''); setRemarks('');
        setCurrency(target.currency ?? target.agent.totals[0]?.currencyCode ?? 'USD');
    }, [target]);

    if (!target) return null;
    const valid = Number(amount) > 0 && /^[A-Z]{3}$/.test(currency) && !!date;

    const save = async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setSaving(true);
        try {
            await AgentCommissionService.record(inst, {
                agentPartyId: target.agent.agentPartyId, buyerOrderId: target.orderId ?? null, settledDate: date,
                amount: Number(amount), currencyCode: currency, reference: reference.trim() || undefined,
                remarks: remarks.trim() || undefined,
            });
            toast.showSuccess('Settlement recorded');
            onSaved();
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to record the settlement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-section="commercial.agentCommission.settleDialog">
            <DialogTitle>{direction === 'PAYABLE' ? 'Record payment to' : 'Record receipt from'} {target.agent.agentName}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2">{target.orderNumber ? `Against order ${target.orderNumber}` : 'On account (not tied to one order)'}</Typography>
                    <TextField type="date" label="Date" value={date} required InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                    <TextField data-help="commercial.agentCommission.field.amount" type="number" label="Amount" value={amount} required
                        inputProps={{ min: 0.01, step: '0.01' }} onChange={e => setAmount(e.target.value)} />
                    <TextField data-help="commercial.agentCommission.field.currency" label="Currency" value={currency} required
                        disabled={!!target.currency} inputProps={{ maxLength: 3 }} onChange={e => setCurrency(e.target.value.toUpperCase())} />
                    <TextField label="Reference" value={reference} inputProps={{ maxLength: 120 }} onChange={e => setReference(e.target.value)} />
                    <TextField label="Remarks" value={remarks} multiline inputProps={{ maxLength: 500 }} onChange={e => setRemarks(e.target.value)} />
                    <Alert severity="info">This only records the settlement here. No accounting entry is posted.</Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!valid || saving}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function VoidDialog({ settlement, onClose, onSaved }: { settlement: ICommissionSettlement | null; onClose: () => void; onSaved: () => void }) {
    const toast = useToast();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => { setReason(''); }, [settlement]);
    if (!settlement) return null;

    const save = async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setSaving(true);
        try {
            await AgentCommissionService.voidSettlement(inst, settlement.id, reason.trim());
            toast.showSuccess('Settlement voided');
            onSaved();
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to void the settlement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Void settlement</DialogTitle>
            <DialogContent>
                <TextField sx={{ mt: 1 }} fullWidth multiline label="Reason" required value={reason}
                    inputProps={{ maxLength: 500 }} onChange={e => setReason(e.target.value)} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={save} disabled={!reason.trim() || saving}>Void</Button>
            </DialogActions>
        </Dialog>
    );
}
